/*
 * Webview / 外链两种场景下的集群解析（对齐 timemark.com 的 src/utils/clusterContext.ts）。
 *
 * - App webview 内：原生通过 H5_GET_COMMON_INFO 返回 cluster（与 timemark.com 共用同一原生
 *   bridge handler，见 src/utils/todayUtils.js 的 getCommonInfo）；原生未返回时（多集群上线前
 *   的旧版本 App）按默认集群 sg 处理。
 * - 浏览器打开的外链（分享/验证/支付等页面）：cluster 由链接的 `?cluster=` 参数决定；旧版本
 *   App 生成的、不带 cluster 参数的链接同样回退到 sg。
 */
import { isInApp, getCommonInfo } from '../todayUtils'
import { DEFAULT_CLUSTER, resolveClusterEndpoint } from './clusters'

let webviewClusterPromise = null

// 匿名可访问页面：登录和未登录用户都能打开，数据只挂在 sg。
// 这些页面强制走 sg 集群，忽略 webview / URL 上的 cluster 参数。
// 这里的 key 用「pages.js 里 chunk 字段归一化后的值」，与实际 URL path 段对应
// （例如 photoVerifyResult 的 chunk 是 'photo-verify-result'，quickPhotoVerify 的 chunk 是 'verify'）。
const ANONYMOUS_FRIENDLY_PAGES = new Set([
  'verifycenter',
  'batchverifyreport',
  'verifymetadata',
  'photoverifyresult',
  'verify' // quickPhotoVerify 在 pages.js 中的 chunk 别名
])

function normalizeRouteSegment(s) {
  return s.toLowerCase().replace(/-/g, '')
}

function isAnonymousFriendlyPage() {
  if (typeof window === 'undefined') return false
  const segments = window.location.pathname.split('/').filter(Boolean)
  return segments.some((seg) =>
    ANONYMOUS_FRIENDLY_PAGES.has(normalizeRouteSegment(seg))
  )
}

/**
 * 获取 webview 集群名（缓存，整个页面生命周期只走一次桥）。
 * 非 App 环境 / 原生未返回 → null（按默认集群 sg 处理）。
 * @returns {Promise<string|null>}
 */
export function ensureWebviewClusterName() {
  if (!isInApp) {
    return Promise.resolve(null)
  }
  if (!webviewClusterPromise) {
    // getCommonInfo 内置超时且从不 reject；超时重试一次，兼顾原生 JSB handler
    // 注册晚于页面首个请求的竞态。
    webviewClusterPromise = (async () => {
      const info = (await getCommonInfo()) ?? (await getCommonInfo())
      return typeof info?.cluster === 'string' && info.cluster
        ? info.cluster
        : null
    })()
  }
  return webviewClusterPromise
}

/**
 * 外链场景：从当前页面 URL 的 `?cluster=` 参数取集群名。
 * @returns {string|null}
 */
export function getClusterFromUrl() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('cluster')
}

/**
 * 解析业务接口 base_url：
 * - App webview 内：由原生 common info 的 cluster 字段决定（缺失回退 sg）
 * - 浏览器内：链接的 `?cluster=` 参数决定（缺失回退 sg）
 * @returns {Promise<string>}
 */
export async function ensureGlobalApiOrigin() {
  // 白名单页面一律走 sg（登录/未登录都能访问，数据只在 sg）
  if (isAnonymousFriendlyPage()) {
    return resolveClusterEndpoint(DEFAULT_CLUSTER)
  }

  if (isInApp) {
    const clusterName = await ensureWebviewClusterName()
    return resolveClusterEndpoint(clusterName)
  }
  return resolveClusterEndpoint(getClusterFromUrl())
}
